import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Building2, 
  Lock, 
  Save, 
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  ScrollText,
  MapPin,
  Clock,
  Timer,
  Globe,
  CalendarDays,
  Plus,
  Trash2,
  PartyPopper,
  Briefcase,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Flag,
  MonitorOff,
  Send,
  Bell,
  Key,
  Copy,
  Check
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import TimezoneSelect from '@/components/TimezoneSelect';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

/** Local YYYY-MM-DD — never use toISOString() (UTC shift breaks calendar dates). */
function toLocalDateStr(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function AdminSettingsPage() {
  const { t, isRTL } = useLanguage();
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [company, setCompany] = useState({ name: '', timezone: 'UTC' });
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [rules, setRules] = useState({
    gps_tracking_interval: 300,  // seconds (default 5 min = 300 sec)
    gps_sync_time: 600,          // seconds (default 10 min = 600 sec)
    idle1_time: 10,
    idle2_time: 30,
    idle3_time: 50,
    next_day_start_time: '00:00',
    device_inactive_time: 30,
    device_inactive_action: 'none'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_type: 'holiday',
    title: '',
    description: '',
    is_recurring: false
  });
  const [workingDays, setWorkingDays] = useState([]);
  const [importingHolidays, setImportingHolidays] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [holidaysCountryCode, setHolidaysCountryCode] = useState('');
  const [loadingPublicHolidays, setLoadingPublicHolidays] = useState(false);

  // Telegram state
  const [telegram, setTelegram] = useState({ enabled: false, bot_token: '', chat_id: '' });
  const [showBotToken, setShowBotToken] = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Partner API keys (admin-only)
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateKeyDialog, setShowCreateKeyDialog] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState(null); // raw key shown once
  const [copiedKey, setCopiedKey] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState(null);
  
  useEffect(() => {
    fetchSettings();
    fetchRules();
    fetchTelegramSettings();
    fetchApiKeys();
  }, []);
  
  useEffect(() => {
    fetchCalendarEvents();
  }, [calendarDate]);
  
  const fetchSettings = async () => {
    try {
      const [profileRes, companyRes] = await Promise.all([
        axios.get(`${API}/admin/settings/profile`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/company`, { withCredentials: true })
      ]);
      setProfile({
        name: profileRes.data.user.name || '',
        email: profileRes.data.user.email || ''
      });
      setCompany({
        name: companyRes.data.name || '',
        timezone: companyRes.data.timezone || 'UTC'
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/rules`, {
        withCredentials: true
      });
      if (response.data.rules) {
        setRules(response.data.rules);
      }
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    }
  };

  const fetchTelegramSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/telegram`, { withCredentials: true });
      setTelegram(response.data);
    } catch (error) {
      console.error('Failed to fetch Telegram settings:', error);
    }
  };

  const fetchApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const response = await axios.get(`${API}/admin/integrations/api-keys`, {
        withCredentials: true,
      });
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    const name = newKeyName.trim();
    if (!name) {
      showMessage('error', 'Enter a name for this API key');
      return;
    }
    setCreatingApiKey(true);
    try {
      const response = await axios.post(
        `${API}/admin/integrations/api-keys`,
        { name },
        { withCredentials: true }
      );
      setCreatedApiKey(response.data);
      setShowCreateKeyDialog(false);
      setNewKeyName('');
      setCopiedKey(false);
      await fetchApiKeys();
      showMessage('success', 'API key created — copy it now, it won’t be shown again');
    } catch (error) {
      showMessage(
        'error',
        error.response?.data?.detail || 'Failed to create API key'
      );
    } finally {
      setCreatingApiKey(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Revoke this API key? Client panels using it will stop working.')) {
      return;
    }
    setRevokingKeyId(keyId);
    try {
      await axios.delete(`${API}/admin/integrations/api-keys/${keyId}`, {
        withCredentials: true,
      });
      await fetchApiKeys();
      showMessage('success', 'API key revoked');
    } catch (error) {
      showMessage(
        'error',
        error.response?.data?.detail || 'Failed to revoke API key'
      );
    } finally {
      setRevokingKeyId(null);
    }
  };

  const copyCreatedKey = async () => {
    if (!createdApiKey?.api_key) return;
    try {
      await navigator.clipboard.writeText(createdApiKey.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      showMessage('error', 'Could not copy — select and copy manually');
    }
  };
  
  const fetchCalendarEvents = async () => {
    setCalendarLoading(true);
    try {
      const month = calendarDate.getMonth() + 1;
      const year = calendarDate.getFullYear();
      
      const response = await axios.get(`${API}/admin/calendar`, {
        params: { month, year },
        withCredentials: true
      });
      
      setCalendarEvents(response.data.events || []);
      setWorkingDays(response.data.working_days || []);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
    } finally {
      setCalendarLoading(false);
    }
  };
  
  const fetchPublicHolidays = async () => {
    setLoadingPublicHolidays(true);
    try {
      const year = calendarDate.getFullYear();
      const response = await axios.get(`${API}/admin/calendar/public-holidays`, {
        params: { year },
        withCredentials: true
      });
      
      setPublicHolidays(response.data.holidays || []);
      setHolidaysCountryCode(response.data.country_code || '');
      setShowImportDialog(true);
    } catch (error) {
      console.error('Failed to fetch public holidays:', error);
      showMessage('error', 'Failed to fetch public holidays');
    } finally {
      setLoadingPublicHolidays(false);
    }
  };
  
  const handleImportHolidays = async () => {
    setImportingHolidays(true);
    try {
      const year = calendarDate.getFullYear();
      const response = await axios.post(
        `${API}/admin/calendar/import-holidays`,
        null,
        { 
          params: { year },
          withCredentials: true 
        }
      );
      
      const { imported_count, skipped_count, country_code } = response.data;
        if (imported_count > 0) {
          const fixed = response.data.absent_records_fixed || 0;
          showMessage(
            'success',
            `Successfully imported ${imported_count} public holidays for ${country_code}${skipped_count > 0 ? ` (${skipped_count} already existed)` : ''}` +
            (fixed > 0 ? `. Fixed ${fixed} wrong absent mark(s).` : '') +
            '. Staff will not be marked absent on these days.'
          );
        } else {
          showMessage('info', `All holidays for ${country_code} are already imported`);
        }
      
      setShowImportDialog(false);
      fetchCalendarEvents();
    } catch (error) {
      console.error('Failed to import holidays:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to import holidays');
    } finally {
      setImportingHolidays(false);
    }
  };
  
  const handleCreateEvent = async () => {
    if (!selectedDate || !eventForm.title) {
      showMessage('error', 'Please fill in all required fields');
      return;
    }
    
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const response = await axios.post(
        `${API}/admin/calendar/event`,
        {
          date: dateStr,
          ...eventForm
        },
        { withCredentials: true }
      );
      
      const fixed = response.data?.absent_records_fixed || 0;
      showMessage(
        'success',
        fixed > 0
          ? `Event saved. ${fixed} auto-absent record(s) converted (don't mark absent on holidays).`
          : 'Event created successfully! Staff will not be marked absent on this day.'
      );
      setShowEventDialog(false);
      setEventForm({ event_type: 'holiday', title: '', description: '', is_recurring: false });
      fetchCalendarEvents();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to create event');
    }
  };
  
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/admin/calendar/event/${eventId}`, {
        withCredentials: true
      });
      showMessage('success', 'Event deleted successfully!');
      fetchCalendarEvents();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete event');
    }
  };
  
  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return calendarEvents.filter(event => event.date === dateStr);
  };
  
  const getDayClassName = (date) => {
    const dateStr = toLocalDateStr(date);
    const events = calendarEvents.filter(event => event.date === dateStr);
    
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const isWorkingDay = workingDays.includes(dayName);
    
    if (events.some(e => e.event_type === 'holiday')) {
      return 'bg-red-100 text-red-700 font-bold rounded-full';
    }
    if (events.some(e => e.event_type === 'company_off')) {
      return 'bg-orange-100 text-orange-700 font-bold rounded-full';
    }
    if (events.some(e => e.event_type === 'staff_leave')) {
      return 'bg-blue-100 text-blue-700 rounded-full';
    }
    if (!isWorkingDay) {
      return 'bg-gray-100 text-gray-500';
    }
    return '';
  };
  
  const navigateMonth = (direction) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarDate(newDate);
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/admin/settings/profile`,
        { name: profile.name },
        { withCredentials: true }
      );
      showMessage('success', 'Profile updated successfully!');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/admin/settings/company`,
        { name: company.name, timezone: company.timezone },
        { withCredentials: true }
      );
      showMessage('success', 'Company settings updated successfully!');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/admin/settings/password`,
        {
          current_password: passwords.current_password,
          new_password: passwords.new_password
        },
        { withCredentials: true }
      );
      showMessage('success', 'Password changed successfully!');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateRules = async (e) => {
    e.preventDefault();
    setSavingRules(true);
    
    try {
      await axios.put(
        `${API}/admin/settings/rules`,
        rules,
        { withCredentials: true }
      );
      showMessage('success', 'Rules updated successfully!');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to update rules');
    } finally {
      setSavingRules(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="relative pb-8" data-testid="admin-settings-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative mx-auto max-w-5xl space-y-5 md:space-y-6">
      <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t("settings.title")}</h2>
          <p className="mt-1.5 text-sm text-indigo-50/90">Manage your account and company settings</p>
        </div>
      </section>
      
      {message.text && (
        <div className={`att-fade-up flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 ${
          message.type === 'success' ? 'bg-indigo-50 text-indigo-800 ring-indigo-200' : ''
        } ${message.type === 'error' ? 'bg-rose-50 text-rose-800 ring-rose-200' : ''}`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-indigo-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-rose-600" />
          )}
          <span className="font-medium">{message.text}</span>
        </div>
      )}
      
      <Tabs defaultValue="profile" className="att-fade-up w-full" style={{ animationDelay: '80ms' }}>
        <div className="w-full overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="inline-flex h-auto w-max min-w-full gap-1 rounded-2xl bg-slate-100/90 p-1.5 justify-start">
            <TabsTrigger
              value="profile"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="profile-tab"
            >
              <User className="mr-2 h-4 w-4 shrink-0" />
              {t('settings.personal_info')}
            </TabsTrigger>
            <TabsTrigger
              value="company"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="company-tab"
            >
              <Building2 className="mr-2 h-4 w-4 shrink-0" />
              {t('companies.title')}
            </TabsTrigger>
            <TabsTrigger
              value="calendar"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="calendar-tab"
            >
              <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
              {t('common.date')}
            </TabsTrigger>
            <TabsTrigger
              value="rules"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="rules-tab"
            >
              <ScrollText className="mr-2 h-4 w-4 shrink-0" />
              {t('settings.rules')}
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="security-tab"
            >
              <Lock className="mr-2 h-4 w-4 shrink-0" />
              {t('settings.security')}
            </TabsTrigger>
            <TabsTrigger
              value="api-keys"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="api-keys-tab"
            >
              <Key className="mr-2 h-4 w-4 shrink-0" />
              API Keys
            </TabsTrigger>
            <TabsTrigger
              value="language"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="language-tab"
            >
              <Globe className="mr-2 h-4 w-4 shrink-0" />
              {t('common.language')}
            </TabsTrigger>
            <TabsTrigger
              value="telegram"
              className="shrink-0 rounded-xl px-3 data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="telegram-tab"
            >
              <Send className="mr-2 h-4 w-4 shrink-0" />
              Telegram
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-5">
          <Card className="overflow-hidden rounded-3xl border-slate-200/70 shadow-sm">
            <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/80 to-cyan-50/50">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <User className="h-4 w-4" />
                </span>
                Profile Information
              </CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Full Name</Label>
                  <Input
                    id="profile-name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Enter your name"
                    className="max-w-md"
                    data-testid="profile-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email Address</Label>
                  <Input
                    id="profile-email"
                    value={profile.email}
                    disabled
                    className="max-w-md bg-gray-50"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-gray-500">Email cannot be changed</p>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="h-11 rounded-2xl bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Company Tab */}
        <TabsContent value="company" className="mt-5">
          <Card className="overflow-hidden rounded-3xl border-slate-200/70 shadow-sm">
            <CardHeader className="border-b bg-gradient-to-r from-indigo-50/80 to-cyan-50/50">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <Building2 className="w-5 h-5" />
                Company Settings
              </CardTitle>
              <CardDescription>Update your company information and timezone</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleUpdateCompany} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    value={company.name}
                    onChange={(e) => setCompany({ ...company, name: e.target.value })}
                    placeholder="Enter company name"
                    className="max-w-md"
                    data-testid="company-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company-timezone" className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    Company Timezone
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">
                    This timezone will be used for attendance reports, payroll calculations, and activity reports.
                  </p>
                  <div className="max-w-md">
                    <TimezoneSelect
                      value={company.timezone}
                      onChange={(tz) => setCompany({ ...company, timezone: tz })}
                      placeholder="Select company timezone..."
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-company-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar Card */}
            <Card className="overflow-hidden rounded-3xl border-slate-200/70 shadow-sm lg:col-span-2">
              <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/80 to-cyan-50/50">
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <CalendarDays className="w-5 h-5" />
                      Company Calendar
                    </CardTitle>
                    <CardDescription>
                      Manage holidays and off days. Holidays and company off days are never marked as absent.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchPublicHolidays}
                      disabled={loadingPublicHolidays}
                      className="bg-white hover:bg-indigo-50 text-indigo-700 border-indigo-300"
                      data-testid="import-holidays-btn"
                    >
                      {loadingPublicHolidays ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Import Public Holidays
                    </Button>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigateMonth(-1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="font-semibold text-lg min-w-[150px] text-center">
                        {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigateMonth(1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {calendarLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-100 border border-red-300"></div>
                        <span>Holiday</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-orange-100 border border-orange-300"></div>
                        <span>Company Off</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-blue-100 border border-blue-300"></div>
                        <span>Staff Leave</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-gray-100 border border-gray-300"></div>
                        <span>Weekend</span>
                      </div>
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600">
                          {day}
                        </div>
                      ))}
                      
                      {/* Calendar Days */}
                      {(() => {
                        const firstDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
                        const lastDay = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
                        const startPadding = firstDay.getDay();
                        const days = [];
                        
                        // Padding for first week
                        for (let i = 0; i < startPadding; i++) {
                          days.push(<div key={`pad-${i}`} className="p-2"></div>);
                        }
                        
                        // Actual days
                        for (let d = 1; d <= lastDay.getDate(); d++) {
                          const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), d);
                          const events = getEventsForDate(date);
                          const isToday = new Date().toDateString() === date.toDateString();
                          
                          days.push(
                            <div
                              key={d}
                              className={`
                                p-2 min-h-[80px] border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors
                                ${isToday ? 'border-blue-500 border-2' : 'border-gray-200'}
                                ${getDayClassName(date)}
                              `}
                              onClick={() => {
                                setSelectedDate(date);
                                if (events.length === 0) {
                                  setShowEventDialog(true);
                                }
                              }}
                            >
                              <div className="font-semibold text-sm">{d}</div>
                              <div className="space-y-1 mt-1">
                                {events.slice(0, 2).map((event, idx) => (
                                  <div
                                    key={idx}
                                    className={`
                                      text-xs px-1 py-0.5 rounded truncate
                                      ${event.event_type === 'holiday' ? 'bg-red-200 text-red-800' : ''}
                                      ${event.event_type === 'company_off' ? 'bg-orange-200 text-orange-800' : ''}
                                      ${event.event_type === 'staff_leave' ? 'bg-blue-200 text-slate-900' : ''}
                                    `}
                                    title={event.title}
                                  >
                                    {event.title}
                                  </div>
                                ))}
                                {events.length > 2 && (
                                  <div className="text-xs text-gray-500">+{events.length - 2} more</div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        
                        return days;
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Events List Card */}
            <Card className="overflow-hidden rounded-3xl border-slate-200/70 shadow-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50/80">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {selectedDate ? (
                      <>
                        <CalendarDays className="w-5 h-5 text-blue-600" />
                        {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </>
                    ) : (
                      <>Events This Month</>
                    )}
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!selectedDate) setSelectedDate(new Date());
                      setShowEventDialog(true);
                    }}
                    data-testid="add-event-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Event
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                {selectedDate ? (
                  // Show events for selected date
                  <div className="divide-y">
                    {getEventsForDate(selectedDate).length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No events on this day</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => setShowEventDialog(true)}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Event
                        </Button>
                      </div>
                    ) : (
                      getEventsForDate(selectedDate).map((event) => (
                        <div key={event.event_id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {event.event_type === 'holiday' && <PartyPopper className="w-4 h-4 text-red-600" />}
                                {event.event_type === 'company_off' && <Briefcase className="w-4 h-4 text-orange-600" />}
                                {event.event_type === 'staff_leave' && <UserCheck className="w-4 h-4 text-blue-600" />}
                                <span className="font-medium">{event.title}</span>
                              </div>
                              {event.description && (
                                <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                              )}
                              <Badge variant="outline" className="mt-2 text-xs">
                                {event.event_type === 'holiday' ? 'Holiday' : 
                                 event.event_type === 'company_off' ? 'Company Off' : 
                                 'Staff Leave'}
                              </Badge>
                            </div>
                            {event.event_type !== 'staff_leave' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEvent(event.event_id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  // Show all events for the month
                  <div className="divide-y">
                    {calendarEvents.filter(e => e.event_type !== 'staff_leave').length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No holidays or off days set</p>
                      </div>
                    ) : (
                      calendarEvents
                        .filter(e => e.event_type !== 'staff_leave')
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map((event) => (
                          <div key={event.event_id} className="p-3 hover:bg-gray-50">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="flex items-center gap-2">
                                  {event.event_type === 'holiday' && <PartyPopper className="w-4 h-4 text-red-600" />}
                                  {event.event_type === 'company_off' && <Briefcase className="w-4 h-4 text-orange-600" />}
                                  <span className="font-medium text-sm">{event.title}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEvent(event.event_id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Add Event Dialog */}
          <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-blue-600" />
                  Add Calendar Event
                </DialogTitle>
                <DialogDescription>
                  {selectedDate && `Add event for ${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select
                    value={eventForm.event_type}
                    onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}
                  >
                    <SelectTrigger data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="holiday">
                        <div className="flex items-center gap-2">
                          <PartyPopper className="w-4 h-4 text-red-600" />
                          Holiday
                        </div>
                      </SelectItem>
                      <SelectItem value="company_off">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-orange-600" />
                          Company Off Day
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="event-title">Title *</Label>
                  <Input
                    id="event-title"
                    placeholder={eventForm.event_type === 'holiday' ? 'e.g., Christmas Day' : 'e.g., Office Closed'}
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    data-testid="event-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="event-description">Description (Optional)</Label>
                  <Input
                    id="event-description"
                    placeholder="Additional details..."
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    data-testid="event-description-input"
                  />
                </div>
                
                {eventForm.event_type === 'holiday' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="recurring"
                        checked={eventForm.is_recurring}
                        onChange={(e) => setEventForm({ ...eventForm, is_recurring: e.target.checked, recurring_type: e.target.checked ? 'yearly' : null })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="recurring" className="text-sm font-normal">
                        Repeat every year
                      </Label>
                    </div>
                    <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2">
                      Don&apos;t mark absent: staff without check-in on this holiday will be marked as Holiday (not Absent).
                    </p>
                  </div>
                )}
                {eventForm.event_type === 'company_off' && (
                  <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2">
                    Don&apos;t mark absent: staff without check-in on this off day will be marked as Day Off (not Absent).
                  </p>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateEvent} data-testid="save-event-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Event
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Import Public Holidays Dialog */}
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-indigo-600" />
                  Import Public Holidays
                </DialogTitle>
                <DialogDescription>
                  Based on your company timezone ({company.timezone}), we detected your country as <strong>{holidaysCountryCode}</strong>.
                  Import public holidays for {calendarDate.getFullYear()}.
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto py-4">
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-slate-900 font-medium">
                    <Globe className="w-4 h-4" />
                    {publicHolidays.length} holidays available for {holidaysCountryCode} in {calendarDate.getFullYear()}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {publicHolidays.map((holiday, idx) => (
                    <div 
                      key={idx} 
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <PartyPopper className="w-4 h-4 text-red-500" />
                        <div>
                          <p className="font-medium text-gray-900">{holiday.name}</p>
                          <p className="text-xs text-gray-500">{holiday.type}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {new Date(holiday.date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleImportHolidays} 
                  disabled={importingHolidays || publicHolidays.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="confirm-import-holidays-btn"
                >
                  {importingHolidays ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Import All Holidays
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* Rules Tab */}
        <TabsContent value="rules" className="mt-5">
          <Card className="overflow-hidden rounded-3xl border-slate-200/70 bg-card shadow-sm">
            <CardHeader className="border-b border-border bg-muted/50 dark:bg-muted/25">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ScrollText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Desktop App Rules
              </CardTitle>
              <CardDescription>Configure tracking intervals and idle time thresholds for the desktop/mobile app</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleUpdateRules} className="space-y-6">
                {/* GPS Tracking Interval */}
                <div className="p-4 rounded-lg border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50 dark:bg-indigo-950/45">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/55">
                      <MapPin className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <Label htmlFor="gps-tracking" className="text-base font-semibold text-indigo-900 dark:text-indigo-100">GPS Tracking Interval</Label>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300/80">How often the app sends location updates</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      id="gps-tracking"
                      type="number"
                      min="1"
                      max="3600"
                      value={rules.gps_tracking_interval}
                      onChange={(e) => setRules({ ...rules, gps_tracking_interval: parseInt(e.target.value) || 300 })}
                      className="w-24 text-center"
                      data-testid="gps-tracking-input"
                    />
                    <span className="text-muted-foreground">seconds</span>
                  </div>
                </div>

                {/* GPS Sync Time */}
                <div className="p-4 rounded-lg border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/45">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/55">
                      <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <Label htmlFor="gps-sync-time" className="text-base font-semibold text-blue-900 dark:text-blue-100">GPS Sync Time</Label>
                      <p className="text-sm text-blue-700 dark:text-blue-300/80">Time interval for syncing GPS data to server</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      id="gps-sync-time"
                      type="number"
                      min="1"
                      max="3600"
                      value={rules.gps_sync_time}
                      onChange={(e) => setRules({ ...rules, gps_sync_time: parseInt(e.target.value) || 600 })}
                      className="w-24 text-center"
                      data-testid="gps-sync-time-input"
                    />
                    <span className="text-muted-foreground">seconds</span>
                  </div>
                </div>
                
                {/* Idle Time Thresholds */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <Timer className="w-5 h-5 text-muted-foreground" />
                    Idle Time Thresholds
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Absolute minutes from idle start (not added): 1 / 2 / 3 → L1 at 1m, L2 at 2m, L3 at 3m.
                  </p>
                  
                  {/* Idle 1 */}
                  <div className="p-4 rounded-lg border border-yellow-200 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-950/40">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50">
                        <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <div>
                        <Label htmlFor="idle1-time" className="text-base font-semibold text-yellow-900 dark:text-yellow-100">Idle Level 1 (Warning)</Label>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300/80">Fires after this many minutes idle</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        id="idle1-time"
                        type="number"
                        min="1"
                        max="120"
                        value={rules.idle1_time}
                        onChange={(e) => setRules({ ...rules, idle1_time: parseInt(e.target.value) || 10 })}
                        className="w-24 text-center"
                        data-testid="idle1-time-input"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                  </div>
                  
                  {/* Idle 2 */}
                  <div className="p-4 rounded-lg border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-950/40">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/50">
                        <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <Label htmlFor="idle2-time" className="text-base font-semibold text-orange-900 dark:text-orange-100">Idle Level 2 (Alert)</Label>
                        <p className="text-sm text-orange-700 dark:text-orange-300/80">Fires after this many minutes idle (not L1+L2)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        id="idle2-time"
                        type="number"
                        min="1"
                        max="120"
                        value={rules.idle2_time}
                        onChange={(e) => setRules({ ...rules, idle2_time: parseInt(e.target.value) || 30 })}
                        className="w-24 text-center"
                        data-testid="idle2-time-input"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                  </div>
                  
                  {/* Idle 3 */}
                  <div className="p-4 rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/40">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
                        <Clock className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <Label htmlFor="idle3-time" className="text-base font-semibold text-red-900 dark:text-red-100">Idle Level 3 (Critical)</Label>
                        <p className="text-sm text-red-700 dark:text-red-300/80">Fires after this many minutes idle (not L1+L2+L3)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        id="idle3-time"
                        type="number"
                        min="1"
                        max="120"
                        value={rules.idle3_time}
                        onChange={(e) => setRules({ ...rules, idle3_time: parseInt(e.target.value) || 50 })}
                        className="w-24 text-center"
                        data-testid="idle3-time-input"
                      />
                      <span className="text-muted-foreground">minutes</span>
                    </div>
                  </div>
                </div>
                
                {/* Start Next Day */}
                <div className="p-4 rounded-lg border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/40">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
                      <CalendarDays className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <Label htmlFor="next-day-start" className="text-base font-semibold text-indigo-900 dark:text-indigo-100">Start Next Day</Label>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300/80">The new working day begins at this time. Affects check-in dates, attendance records, and all reports company-wide.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      id="next-day-start"
                      type="time"
                      value={rules.next_day_start_time}
                      onChange={(e) => setRules({ ...rules, next_day_start_time: e.target.value || '00:00' })}
                      className="w-36"
                      data-testid="next-day-start-time-input"
                    />
                  </div>
                </div>
                
                {/* Device Inactive Rule */}
                <div className="p-4 rounded-lg border border-border bg-muted/50 dark:bg-muted/25">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <MonitorOff className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-foreground">Device Inactive</Label>
                      <p className="text-sm text-muted-foreground">Action to take when no activity is detected from any device (web, desktop, mobile)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Input
                        id="device-inactive-time"
                        type="number"
                        min="1"
                        max="480"
                        value={rules.device_inactive_time}
                        onChange={(e) => setRules({ ...rules, device_inactive_time: parseInt(e.target.value) || 30 })}
                        className="w-24 text-center"
                        data-testid="device-inactive-time-input"
                      />
                      <span className="text-muted-foreground text-sm">min</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">Action:</Label>
                      <select
                        value={rules.device_inactive_action}
                        onChange={(e) => setRules({ ...rules, device_inactive_action: e.target.value })}
                        className="border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-background text-foreground"
                        data-testid="device-inactive-action-select"
                      >
                        <option value="none">None</option>
                        <option value="start_idle">Start Idle</option>
                        <option value="force_checkout">Force Check-out</option>
                        <option value="force_break">Force Break</option>
                      </select>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={savingRules}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-rules-btn"
                >
                  {savingRules ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Rules
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Rules Info Card */}
          <Card className="mt-6 border-l-4 border-l-indigo-500 bg-card">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-foreground mb-3">How It Works</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">GPS Tracking Interval:</strong> How frequently the app captures GPS coordinates (in seconds)</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">GPS Sync Time:</strong> How often captured GPS data is synced to the server (in seconds)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Idle 1:</strong> First warning shown to staff when no activity detected</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Idle 2:</strong> Stronger alert with notification to staff</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <span><strong className="text-foreground">Idle 3:</strong> Critical threshold - may automatically start a break</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Security Tab */}
        <TabsContent value="security" className="mt-5">
          <Card className="overflow-hidden rounded-3xl border-slate-200/70 bg-card shadow-sm">
            <CardHeader className="border-b border-border bg-muted/50 dark:bg-muted/25">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative max-w-md">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwords.current_password}
                      onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                      placeholder="Enter current password"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative max-w-md">
                    <Input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwords.new_password}
                      onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative max-w-md">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwords.confirm_password}
                      onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="change-password-btn"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Security Tips */}
          <Card className="mt-6 border-l-4 border-l-amber-500 bg-card">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-foreground mb-3">Security Tips</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" />
                  Use a strong password with at least 8 characters
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" />
                  Include uppercase, lowercase, numbers, and symbols
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" />
                  Do not reuse passwords from other accounts
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-indigo-500 mt-0.5" />
                  Change your password regularly
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="mt-5 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                {t('settings.language_settings')}
              </CardTitle>
              <CardDescription>{t('settings.select_language')}</CardDescription>
            </CardHeader>
            <CardContent>
              <LanguageSelector showLabel className="max-w-md" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Choose light, dark, or match your system theme</CardDescription>
            </CardHeader>
            <CardContent>
              <ThemeToggle />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Telegram Alerts Tab */}
        <TabsContent value="telegram" className="mt-5">
          <div className="max-w-4xl space-y-1">
            <h2 className="text-2xl font-bold text-gray-900">Telegram Notifications</h2>
            <p className="text-sm text-gray-500">
              Receive alerts for staff attendance, check-ins, check-outs and idle events via Telegram bot.
            </p>
          </div>

          <div className="mt-5 grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
            {/* LEFT COLUMN: Configuration form */}
            <div className="space-y-6">
            {/* Main card */}
            <Card className="shadow-sm border border-gray-200">
              <CardContent className="p-6 space-y-6">

                {/* Toggle row */}
                <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="font-semibold text-gray-900">Telegram Alerts</p>
                    <p className="text-sm text-gray-400 mt-0.5">
                      {telegram.enabled ? 'Notifications are active' : 'Notifications are disabled'}
                    </p>
                  </div>
                  <Switch
                    checked={telegram.enabled}
                    onCheckedChange={(v) => setTelegram({ ...telegram, enabled: v })}
                    data-testid="telegram-enabled-toggle"
                  />
                </div>

                {/* BOT TOKEN */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
                    Bot Token
                  </Label>
                  <div className="relative">
                    <Input
                      type={showBotToken ? 'text' : 'password'}
                      placeholder="Enter your Telegram bot token"
                      value={telegram.bot_token}
                      onChange={(e) => setTelegram({ ...telegram, bot_token: e.target.value })}
                      className="pr-10 rounded-xl border-gray-200 focus:ring-2 focus:ring-indigo-300"
                      data-testid="telegram-bot-token-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowBotToken(!showBotToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showBotToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-indigo-500">
                    Get from{' '}
                    <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-700">
                      @BotFather
                    </a>{' '}
                    on Telegram
                  </p>
                </div>

                {/* CHAT ID */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
                    Chat ID
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. 6822492318"
                    value={telegram.chat_id}
                    onChange={(e) => setTelegram({ ...telegram, chat_id: e.target.value })}
                    className="rounded-xl border-gray-200 focus:ring-2 focus:ring-indigo-300"
                    data-testid="telegram-chat-id-input"
                  />
                  <p className="text-xs text-gray-400">
                    Send a message to your bot, then visit{' '}
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-600 text-xs">
                      https://api.telegram.org/bot&#123;TOKEN&#125;/getUpdates
                    </code>{' '}
                    to find your Chat ID.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <Button
                onClick={async () => {
                  setSavingTelegram(true);
                  try {
                    await axios.post(`${API}/admin/settings/telegram`, telegram, { withCredentials: true });
                    showMessage('success', 'Telegram settings saved successfully!');
                  } catch {
                    showMessage('error', 'Failed to save Telegram settings.');
                  } finally {
                    setSavingTelegram(false);
                  }
                }}
                disabled={savingTelegram}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-5"
                data-testid="telegram-save-btn"
              >
                {savingTelegram
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  : <><Send className="w-4 h-4 mr-2" />Save Settings</>}
              </Button>

              <Button
                variant="outline"
                onClick={async () => {
                  setSendingTest(true);
                  try {
                    await axios.post(`${API}/admin/settings/telegram/test`, {}, { withCredentials: true });
                    showMessage('success', 'Test message sent! Check your Telegram.');
                  } catch {
                    showMessage('error', 'Failed to send test message. Check your Bot Token and Chat ID.');
                  } finally {
                    setSendingTest(false);
                  }
                }}
                disabled={sendingTest || !telegram.bot_token || !telegram.chat_id}
                className="rounded-xl px-5 border-gray-300"
                data-testid="telegram-test-btn"
              >
                {sendingTest
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  : <><Send className="w-4 h-4 mr-2" />Send Test Message</>}
              </Button>
            </div>
            </div>

            {/* RIGHT COLUMN: Setup Guide */}
            <div>
              <Card className="border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white shadow-sm">
                <CardHeader className="pb-3 border-b border-indigo-100">
                  <CardTitle className="flex items-center gap-2 text-slate-900 text-base">
                    <Bell className="w-4 h-4" />
                    How to Set Up Your Telegram Bot
                  </CardTitle>
                  <CardDescription className="text-indigo-600 text-xs">
                    Follow these steps to connect Trackly to Telegram
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">

                  {/* Step 1 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">1</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Open Telegram &amp; find BotFather</p>
                      <p className="text-xs text-gray-500 mt-0.5">Search for{' '}
                        <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-semibold underline">@BotFather</a>
                        {' '}in Telegram and open a chat with it.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">2</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Create a new bot</p>
                      <p className="text-xs text-gray-500 mt-0.5">Send the command:</p>
                      <code className="inline-block mt-1 bg-indigo-100 text-slate-900 text-xs px-2 py-1 rounded-lg font-mono">/newbot</code>
                      <p className="text-xs text-gray-500 mt-1.5">BotFather will ask for a name and a username ending in <code className="bg-gray-100 px-1 rounded text-xs">bot</code> (e.g. <em>TracklyAlerts_bot</em>).</p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">3</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Copy your Bot Token</p>
                      <p className="text-xs text-gray-500 mt-0.5">BotFather will give you a token that looks like:</p>
                      <code className="inline-block mt-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-lg font-mono break-all">123456789:ABCdefGhIJKlmNoPQRstuVWXyz</code>
                      <p className="text-xs text-gray-500 mt-1.5">Paste it into the <strong>Bot Token</strong> field on the left.</p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">4</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Start your bot</p>
                      <p className="text-xs text-gray-500 mt-0.5">Open the bot you just created in Telegram and send it any message (e.g. <em>"Hi"</em>). This activates the chat so Trackly can send you messages.</p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">5</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Find your Chat ID</p>
                      <p className="text-xs text-gray-500 mt-0.5">Open this URL in your browser <em>(replace the token with yours)</em>:</p>
                      <code className="inline-block mt-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-lg font-mono break-all">
                        https://api.telegram.org/bot<span className="text-indigo-600">&#123;YOUR_TOKEN&#125;</span>/getUpdates
                      </code>
                      <p className="text-xs text-gray-500 mt-1.5">In the response, look for <code className="bg-gray-100 px-1 rounded text-xs font-mono">"id"</code> inside the <code className="bg-gray-100 px-1 rounded text-xs font-mono">chat</code> object:</p>
                      <code className="inline-block mt-1 bg-gray-800 text-green-400 text-xs px-3 py-2 rounded-lg font-mono w-full">
                        "chat": &#123; <strong className="text-yellow-300">"id": 123456789</strong>, ... &#125;
                      </code>
                      <p className="text-xs text-gray-500 mt-1.5">Copy that number and paste it into the <strong>Chat ID</strong> field.</p>
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">6</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">Save &amp; Test</p>
                      <p className="text-xs text-gray-500 mt-0.5">Click <strong>Save Settings</strong>, then click <strong>Send Test Message</strong>. You should receive a message in your Telegram bot instantly!</p>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </div>

          </div>
        </TabsContent>

        {/* Partner API Keys — admin only */}
        <TabsContent value="api-keys" className="mt-5">
          <div className="max-w-3xl space-y-1">
            <h2 className="text-2xl font-bold text-gray-900">Partner API Keys</h2>
            <p className="text-sm text-gray-500">
              Create keys for external panels. Clients use the key with{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">GET /api/v1/attendance</code>
              {' '}— they do not manage keys themselves.
            </p>
          </div>

          <Card className="mt-5 max-w-3xl shadow-sm border border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b border-slate-100">
              <div>
                <CardTitle className="text-base">Your keys</CardTitle>
                <CardDescription>Up to 10 active keys. Raw secret is shown only once on create.</CardDescription>
              </div>
              <Button
                onClick={() => {
                  setNewKeyName('');
                  setShowCreateKeyDialog(true);
                }}
                className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-api-key-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create key
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {apiKeysLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Loading…
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">
                  No API keys yet. Create one to share with a client panel.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {apiKeys.map((k) => (
                    <li
                      key={k.key_id}
                      className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                      data-testid={`api-key-row-${k.key_id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900 truncate">{k.name}</p>
                          <Badge
                            className={
                              k.status === 'active'
                                ? 'bg-emerald-100 text-emerald-700 border-0'
                                : 'bg-slate-100 text-slate-600 border-0'
                            }
                          >
                            {k.status}
                          </Badge>
                        </div>
                        <p className="mt-1 text-xs text-slate-500 font-mono">
                          {k.key_prefix}… · {k.key_id}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          Created {k.created_at ? new Date(k.created_at).toLocaleString() : '—'}
                          {k.last_used_at
                            ? ` · Last used ${new Date(k.last_used_at).toLocaleString()}`
                            : ' · Never used'}
                        </p>
                      </div>
                      {k.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50 shrink-0"
                          disabled={revokingKeyId === k.key_id}
                          onClick={() => handleRevokeApiKey(k.key_id)}
                          data-testid={`revoke-api-key-${k.key_id}`}
                        >
                          {revokingKeyId === k.key_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1.5" />
                              Revoke
                            </>
                          )}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Dialog open={showCreateKeyDialog} onOpenChange={setShowCreateKeyDialog}>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create API key</DialogTitle>
                <DialogDescription>
                  Give it a label (e.g. “Client Panel”). The full key is shown only once after create.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="api-key-name">Name</Label>
                <Input
                  id="api-key-name"
                  placeholder="Client Panel"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="rounded-xl"
                  data-testid="api-key-name-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateApiKey();
                  }}
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => setShowCreateKeyDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                  disabled={creatingApiKey}
                  onClick={handleCreateApiKey}
                  data-testid="confirm-create-api-key-btn"
                >
                  {creatingApiKey ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={!!createdApiKey}
            onOpenChange={(open) => {
              if (!open) setCreatedApiKey(null);
            }}
          >
            <DialogContent className="rounded-2xl sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Copy your API key</DialogTitle>
                <DialogDescription>
                  This is the only time the full key is shown. Store it securely for the client panel.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm font-medium text-slate-700">{createdApiKey?.name}</p>
                <div className="flex gap-2">
                  <code
                    className="flex-1 break-all rounded-xl bg-slate-900 px-3 py-3 text-xs text-emerald-300 font-mono"
                    data-testid="created-api-key-value"
                  >
                    {createdApiKey?.api_key}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl shrink-0"
                    onClick={copyCreatedKey}
                    data-testid="copy-api-key-btn"
                  >
                    {copiedKey ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  Endpoints for the client:{' '}
                  <span className="font-mono">GET /api/v1/attendance</span> and{' '}
                  <span className="font-mono">GET /api/v1/attendance/&#123;staff_id&#125;</span>
                </p>
              </div>
              <DialogFooter>
                <Button
                  className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setCreatedApiKey(null)}
                >
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}

export default AdminSettingsPage;
